import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar, Eye, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BackButton } from "@/components/BackButton/BackButton";
import { Badge } from "@/components/ui/badge";
import ResourceDetailsContentBlock from "@/modules/hub/features/ResourceLibraryV2/components/ResourceDetailsContentBlock/ResourceDetailsContentBlock";
import ResourceDetailsSkeleton from "../ResourceDetailsSkeleton/ResourceDetailsSkeleton";
import { FavoriteButton } from "@/modules/hub/features/ResourceLibraryV2/components/FavoriteButton/FavoriteButton";
import { ShareToEmployeeButton } from "@/modules/hub/features/ResourceLibraryV2/components/ShareToEmployeeButton/ShareToEmployeeButton";
import { updateResourceViewCount } from "@/api/updateResourceViewCount/updateResourceViewCountApi";
import { toggleResourceFavorite } from "@/api/favoriteResource/favoriteResourceApi";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { getResourceDetails } from "@/modules/hub/features/ResourceLibraryV2/api/getResourceDetails/getResourceDetailsApi";
import { GetResourceDetailsResponse } from "@/modules/hub/features/ResourceLibraryV2/api/getResourceDetails/types";
import { VideoEmbed } from "@/modules/hub/features/ResourceLibraryV2/components/VideoEmbed/VideoEmbed";
import { PodcastEmbed } from "@/modules/hub/features/ResourceLibraryV2/components/PodcastEmbed/PodcastEmbed";
import { FileEmbed } from "@/modules/hub/features/ResourceLibraryV2/components/FileEmbed/FileEmbed";
import { ExternalLink } from "lucide-react";
import ShowIfHasAccess from "@/components/ShowIfHasAccess/ShowIfHasAccess";
import { trackPodcastDetailsView } from "@/modules/hub/features/Podcasts/analytics/podcastsAnalytics";
import { trackNewsDetailsView } from "@/modules/hub/features/News/analytics/newsAnalytics";
import { trackResourceDetailsView } from "@/modules/hub/features/ResourceLibraryV2/analytics/resourceLibraryAnalytics";

export default function ResourceDetails() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [resourceNotFound, setResourceNotFound] = useState<boolean>(false);
  const [resource, setResource] = useState<
    GetResourceDetailsResponse["data"] | null
  >(null);
  const [isFavorited, setIsFavorited] = useState<boolean>(false);
  const trackedResourceUuid = useRef<string | null>(null);

  useEffect(() => {
    async function fetchData() {
      if (!slug) return;
      setLoading(true);
      setResourceNotFound(false);

      try {
        const response = await getResourceDetails(slug);
        const resourceData = response.data;

        if (!resourceData || resourceData.id === null) {
          setResourceNotFound(true);
        } else {
          // Check if this is a Partner Page that should redirect
          if (
            resourceData.typeName === "Partner Page" &&
            resourceData.contentUrl
          ) {
            window.open(
              resourceData.contentUrl,
              "_blank",
              "noopener,noreferrer",
            );
            navigate("/hub/resources-v2");
            return;
          }
          setResource(resourceData);
          setIsFavorited(resourceData.isFavorited);
        }
      } catch (error) {
        console.error("Failed to fetch resource:", error);
        setResourceNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [slug, navigate]);

  const incrementViewCount = async () => {
    if (!resource?.uuid) return;
    try {
      await updateResourceViewCount({ resourceUuid: resource.uuid });
    } catch (error) {
      console.error("Error incrementing view count:", error);
    }
  };

  useEffect(() => {
    if (resource && !loading) {
      const timer = setTimeout(() => {
        incrementViewCount();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resource, loading]);

  useEffect(() => {
    if (
      resource?.uuid &&
      resource.typeName &&
      trackedResourceUuid.current !== resource.uuid
    ) {
      if (resource.typeName === "Podcast") {
        trackPodcastDetailsView({
          resourceType: resource.typeName,
          resourceUuid: resource.uuid,
        });
        trackedResourceUuid.current = resource.uuid;
      } else if (resource.typeName === "News") {
        trackNewsDetailsView({
          resourceType: resource.typeName,
          resourceUuid: resource.uuid,
        });
        trackedResourceUuid.current = resource.uuid;
      } else {
        trackResourceDetailsView({
          resourceType: resource.typeName,
          resourceUuid: resource.uuid,
        });
        trackedResourceUuid.current = resource.uuid;
      }
    }
  }, [resource?.uuid, resource?.typeName]);

  const handleToggleFavorite = async () => {
    if (!resource?.uuid) return;
    try {
      const response = await toggleResourceFavorite({
        resourceUuid: resource.uuid,
      });
      setIsFavorited(response.data.favorited);
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  if (resourceNotFound) {
    return (
      <MainPageWrapper hideHeader title="Resource Library">
        <div className="text-center py-16">
          <h1 className="text-xl font-medium text-gray-900 dark:text-slate-100 mb-2">
            Resource Not Found
          </h1>
          <p className="mb-8 text-gray-500 dark:text-gray-400">
            The resource you&apos;re looking for doesn&apos;t exist or has been
            removed.
          </p>
          <BackButton />
        </div>
      </MainPageWrapper>
    );
  }

  if (loading) {
    return (
      <MainPageWrapper hideHeader title="Resource Library">
        <ResourceDetailsSkeleton />
      </MainPageWrapper>
    );
  }

  if (!resource) {
    return (
      <MainPageWrapper hideHeader title="Resource Library">
        <div className="text-center py-16">
          <h1 className="text-xl font-medium text-gray-900 dark:text-slate-100 mb-2">
            Resource Not Found
          </h1>
          <p className="mb-8 text-gray-500 dark:text-gray-400">
            The resource you&apos;re looking for doesn&apos;t exist or has been
            removed.
          </p>
          <BackButton />
        </div>
      </MainPageWrapper>
    );
  }

  const {
    title,
    thumbnailUrl,
    thumbnail2Url,
    contentUrl,
    typeName,
    viewCount,
    publishStartDate,
    categories,
    trades,
    roles,
    contentBlocks,
    icon,
    backgroundColor,
    textColor,
    borderColor,
    relatedResources,
  } = resource;

  // Articles show the thumbnail2 image, falling back to the standard thumbnail
  // when they don't have one yet. All other types keep the standard thumbnail.
  const headerThumbnailUrl =
    typeName === "Article" ? (thumbnail2Url ?? thumbnailUrl) : thumbnailUrl;

  return (
    <MainPageWrapper hideHeader title="Resource Details">
      <div>
        <div className="mb-8">
          <BackButton />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-8">
            {/* Compact header: thumbnail + info (mirrors EventDetails) */}
            <div className="flex gap-6">
              {typeName !== "Video" &&
                (headerThumbnailUrl ? (
                  <div className="flex-shrink-0 w-48 self-start rounded-lg overflow-hidden">
                    <img
                      alt={title || "Resource Thumbnail"}
                      className="w-full h-auto"
                      src={headerThumbnailUrl}
                    />
                  </div>
                ) : (
                  <div className="flex-shrink-0 w-48 aspect-video rounded-lg bg-gray-100 dark:bg-slate-800 flex items-center justify-center">
                    <span className="text-sm text-gray-400 dark:text-gray-500">
                      No Image
                    </span>
                  </div>
                ))}

              <div className="min-w-0 flex-1">
                {/* Title and Actions Row */}
                <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-slate-100 tracking-tight leading-tight">
                      {title}
                    </h1>
                  </div>
                  <div className="flex items-center gap-1">
                    <ShowIfHasAccess
                      requiredPermissions={["administration-resource-library"]}
                    >
                      <Button
                        className="h-9 w-9 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                        onClick={() =>
                          window.open(
                            `/admin/resources/${resource.uuid}/edit`,
                            "_blank",
                          )
                        }
                        size="icon"
                        title="Edit resource in new tab"
                        variant="ghost"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </ShowIfHasAccess>
                    <FavoriteButton
                      isFavorited={isFavorited}
                      onToggle={handleToggleFavorite}
                    />
                    {resource.uuid && (
                      <ShareToEmployeeButton
                        resourceTitle={title || "Resource"}
                        resourceUuid={resource.uuid}
                      />
                    )}
                  </div>
                </div>

                {/* Metadata Row */}
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                  <div
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                    style={{
                      backgroundColor: backgroundColor ?? "#f3f4f6",
                      color: textColor ?? "#374151",
                      border: borderColor ? `1px solid ${borderColor}` : "none",
                    }}
                  >
                    <span dangerouslySetInnerHTML={{ __html: icon ?? "" }} />
                    <span>{typeName}</span>
                  </div>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span className="flex items-center gap-1.5">
                    <Eye size={14} />
                    {viewCount || 0} views
                  </span>
                </div>
              </div>
            </div>

            {/* Tags + content wrapper so the "Header Content Section" closing tags still match */}
            <div>
              {/* Tags - Simplified into a single flowing section */}
              {((categories && categories.length > 0) ||
                (trades && trades.length > 0) ||
                (roles && roles.length > 0)) && (
                <div className="flex flex-wrap gap-2">
                  {categories?.map((category) => (
                    <span
                      className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400"
                      key={`cat-${category.id}`}
                    >
                      {category.name}
                    </span>
                  ))}
                  {trades?.map((trade) => (
                    <span
                      className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400"
                      key={`trade-${trade.id}`}
                    >
                      {trade.name}
                    </span>
                  ))}
                  {roles?.map((role) => (
                    <span
                      className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400"
                      key={`role-${role.id}`}
                    >
                      {role.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              {/* Video Resource */}
              {typeName === "Video" && contentUrl && (
                <div className="mb-8">
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                    <VideoEmbed contentUrl={contentUrl} />
                  </div>
                </div>
              )}

              {/* Podcast Resource */}
              {typeName === "Podcast" && contentUrl && (
                <div className="mb-8">
                  <PodcastEmbed contentUrl={contentUrl} />
                </div>
              )}

              {/* File Resource - using FileEmbed component */}
              {typeName === "File" && resource?.file && (
                <FileEmbed
                  bypassIframeForPdf={true}
                  contentType={resource.file.contentType}
                  contentUrl={resource.file.url || ""}
                  fileSize={resource.file.size}
                  title={resource.file.name}
                />
              )}

              {typeName === "Series" &&
                resource?.seriesEntries &&
                resource.seriesEntries.length > 0 && (
                  <div className="space-y-8">
                    {resource.seriesEntries.map((entry, index) => (
                      <div className="space-y-4" key={entry.id}>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
                            {entry.title}
                          </h3>
                        </div>

                        {/* Render Video or Podcast embed based on type */}
                        {entry.resourceType === "Video" && entry.contentUrl && (
                          <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                            <VideoEmbed contentUrl={entry.contentUrl} />
                          </div>
                        )}

                        {entry.resourceType === "Podcast" &&
                          entry.contentUrl && (
                            <PodcastEmbed contentUrl={entry.contentUrl} />
                          )}

                        {/* Link to individual resource */}
                        <div className="text-sm">
                          <Link
                            className="text-primary hover:underline inline-flex items-center gap-1"
                            to={`/hub/resources-v2/${entry.slug}`}
                          >
                            Full resource page
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {/* Content Blocks */}
              {contentBlocks && contentBlocks.length > 0 && (
                <div
                  className={`space-y-6 ${typeName === "Series" && resource?.seriesEntries && resource.seriesEntries.length > 0 ? "mt-8" : ""}`}
                >
                  {contentBlocks.map((block, index) => (
                    <ResourceDetailsContentBlock
                      block={block}
                      key={block.id || index}
                    />
                  ))}
                </div>
              )}

              {/* No content message */}
              {!contentUrl &&
                (!contentBlocks || contentBlocks.length === 0) &&
                typeName !== "File" &&
                typeName !== "Series" && (
                  <div className="text-center py-12 text-gray-400 dark:text-gray-500">
                    <p>No detailed content available for this resource.</p>
                  </div>
                )}
            </div>
          </div>

          {/* Sidebar */}
          <div>
            <div className="sticky top-6">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-4">
                Recommended
              </h3>
              {relatedResources && relatedResources.length > 0 ? (
                <div className="space-y-5">
                  {relatedResources.map((rel, idx) => (
                    <a
                      className="group flex items-start gap-3"
                      href={`/hub/resources-v2/${rel.slug}`}
                      key={idx}
                    >
                      <div className="relative w-16 shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-800">
                        <div className="relative aspect-video">
                          {rel.thumbnailUrl ? (
                            <img
                              alt={rel.title}
                              className="object-contain w-full h-full absolute inset-0"
                              src={rel.thumbnailUrl}
                            />
                          ) : (
                            <div className="w-full h-full absolute inset-0 flex items-center justify-center">
                              <span
                                className="inline-block scale-50 opacity-50"
                                dangerouslySetInnerHTML={{
                                  __html: rel.primaryIcon ?? "",
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-slate-200 group-hover:text-primary dark:group-hover:text-primary truncate">
                          {rel.title}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {rel.resourceType}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  No recommendations yet.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainPageWrapper>
  );
}
